/**
 * Typed reader + validator for public/data/georgi-n1.manifest.json (schema georgi-n1/1).
 *
 * Pure TypeScript: no Phaser import, so the contract can be pinned by Vitest without a browser.
 * The validator fails loudly with every problem it found — a broken asset drop must never load.
 */

export const N1_SCHEMA = 'georgi-n1/1';
export const N1_MASTER_FRAMES = 80;
export const N1_PIVOT = { x: 0.5, y: 0.78125 } as const;

export const N1_ACTIONS = [
  'idle-loop',
  'idle-to-walk',
  'walk-loop',
  'walk-to-run',
  'run-loop',
  'run-to-walk',
  'run-to-jump',
  'jump-to-run',
  'jump-to-idle',
  'run-to-brake-to-idle',
  'walk-to-brake-to-idle',
  'walk-turn',
  'run-turn',
] as const;
export type N1ActionId = (typeof N1_ACTIONS)[number];

export const N1_TIERS = { keys: 20, smooth: 40 } as const;
export type N1Tier = keyof typeof N1_TIERS;

export const N1_TIER_LABELS = ['keys@1x', 'smooth@1x', 'keys@2x', 'smooth@2x'] as const;
export type N1TierLabel = (typeof N1_TIER_LABELS)[number];

export const N1_AERIAL: readonly N1ActionId[] = ['run-to-jump', 'jump-to-run', 'jump-to-idle'];

export interface N1Contact {
  action: N1ActionId;
  frame: number;
  mirrored: boolean;
}

export interface N1ActionTier {
  frames: number;
  /** Master frames this tier samples (round(i·79/(n−1))); null for a rebuilt loop cycle. */
  masterIndices: number[] | null;
  /** For a rebuilt loop cycle: the cycle frames this tier shows (smooth = all, keys = every other). */
  cycleIndices?: number[] | null;
  frameMs: number;
  rootLift: number[] | null;
}

/**
 * A loop rebuilt by tools/normalize/loop_cycle.py (pipeline loop-cycle/1): the authored keys were
 * re-sequenced into a gait cycle, torso-registered to the anchor key (frame 0 stays the chain
 * contact) and in-betweened with optical-flow morphs instead of the V8 cross-dissolves.
 */
export interface N1Cycle {
  pipeline: string;
  /** A wrapping loop cycle or a one-shot transition pinned to its two chain contacts. */
  kind: 'loop' | 'one-shot';
  /** Cycle frames (= the smooth tier's frame count for this action). */
  frames: number;
  anchor: number;
  /** Authored keys in gait order, anchor first. */
  order: number[];
  droppedKeys: number[];
  metrics: Record<string, unknown>;
  [key: string]: unknown;
}

export interface N1Action {
  kind: 'loop' | 'transition';
  facing: 'left';
  mirror: true;
  from: string;
  to: string;
  facingChange: boolean;
  loop: boolean;
  authoredKeys: number;
  motion: string | null;
  review: { frameMs: number; durationMs: number; fps: number };
  masterFrames: number;
  feetLineY: number;
  rootLift: { master: number[]; shiftPx: number[]; keyLift: number[] } | null;
  contacts: { first: N1Contact | null; last: N1Contact | null };
  tiers: Record<N1Tier, N1ActionTier>;
  /** Present when the action was rebuilt from its authored keys (all 13 are); null for a V8-decimated action. */
  cycle: N1Cycle | null;
  provenance: Record<string, unknown>;
}

export interface N1TierInfo {
  tier: N1Tier;
  scale: number;
  page: number;
  root: [number, number];
  multiatlas: string;
  pages: string[];
  hashJson: string[];
  /** Default frames per action on this tier; rebuilt cycles (`cycleActions`) declare their own. */
  framesPerAction: number;
  /** Total frames in the atlas = Σ actions[id].tiers[tier].frames. */
  frameCount: number;
  cycleActions?: string[];
}

export interface N1ChainStep {
  action: N1ActionId;
  facing: 'left' | 'right';
}

export interface N1Manifest {
  schema: typeof N1_SCHEMA;
  character: string;
  release: string;
  sourceRelease: string;
  generatedAt: string;
  geometry: {
    page: number;
    root: [number, number];
    pivot: [number, number];
    bodyHeightPx: number;
    jumpHeightPx: number;
    mobileTextureMax: number;
    padding: number;
    scales: Record<string, number>;
  };
  actions: Record<N1ActionId, N1Action>;
  states: string[];
  transitions: { from: string; to: string; action: N1ActionId; facingChange: boolean }[];
  missingTransitions: string[];
  chains: Record<string, N1ChainStep[]>;
  hitboxes: { authored: boolean; body: Rect; torso: Rect };
  feetLineY: Record<N1ActionId, number>;
  tiers: Record<N1TierLabel, N1TierInfo>;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export class N1ManifestError extends Error {
  constructor(public readonly problems: string[]) {
    super(
      `N1 manifest rejected (${problems.length} problem${problems.length === 1 ? '' : 's'}):\n - ${problems.join('\n - ')}`,
    );
    this.name = 'N1ManifestError';
  }
}

/** Frame name inside the atlas: `<action>/<index:02d>` (index within the tier). */
export function frameName(action: N1ActionId, index: number): string {
  return `${action}/${String(index).padStart(2, '0')}`;
}

/** `<action>/<index:02d>` → index within the tier (0 when the name is not an N1 frame name). */
export function frameIndexOf(name: string): number {
  const n = Number(name.slice(name.lastIndexOf('/') + 1));
  return Number.isFinite(n) ? n : 0;
}

/** Master indices a tier of `n` frames samples: round(i·79/(n−1)); frames 0 and 79 always survive. */
export function expectedMasterIndices(n: number): number[] {
  return Array.from({ length: n }, (_, i) => Math.round((i * (N1_MASTER_FRAMES - 1)) / (n - 1)));
}

export function tierOfLabel(label: N1TierLabel): { tier: N1Tier; scale: number } {
  const [tier, scale] = label.split('@') as [N1Tier, string];
  return { tier, scale: scale === '2x' ? 1 : 0.5 };
}

export function tierFrameNames(manifest: N1Manifest, action: N1ActionId, tier: N1Tier): string[] {
  const n = manifest.actions[action].tiers[tier].frames;
  return Array.from({ length: n }, (_, i) => frameName(action, i));
}

export function tierFrameMs(manifest: N1Manifest, action: N1ActionId, tier: N1Tier): number {
  return manifest.actions[action].tiers[tier].frameMs;
}

/** Frames a tier's atlas must hold: the sum over the 13 actions (cycles differ from the default). */
export function tierFrameCount(manifest: N1Manifest, tier: N1Tier): number {
  return N1_ACTIONS.reduce((sum, id) => sum + manifest.actions[id].tiers[tier].frames, 0);
}

/** Lift (px, positive = up) to apply to a tier frame when reproducing the authored jump arc. */
export function rootLiftPx(
  manifest: N1Manifest,
  action: N1ActionId,
  label: N1TierLabel,
  index: number,
): number {
  const { tier, scale } = tierOfLabel(label);
  const lift = manifest.actions[action].tiers[tier].rootLift;
  return lift ? (lift[index] ?? 0) * scale : 0;
}

// ----------------------------------------------------------------------------- validation

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isNumArray = (v: unknown, len?: number): v is number[] =>
  Array.isArray(v) && (len === undefined || v.length === len) && v.every(isNum);

/** Validate an untrusted JSON value against the georgi-n1/1 contract. Throws N1ManifestError. */
export function parseN1Manifest(input: unknown): N1Manifest {
  const problems: string[] = [];
  const fail = (msg: string) => problems.push(msg);
  if (!isObj(input)) throw new N1ManifestError(['manifest is not an object']);

  if (input.schema !== N1_SCHEMA) fail(`schema is ${String(input.schema)}, expected ${N1_SCHEMA}`);

  const geometry = isObj(input.geometry) ? input.geometry : undefined;
  if (!geometry) fail('geometry missing');
  else {
    if (geometry.page !== 640) fail(`geometry.page is ${String(geometry.page)}, expected 640`);
    if (!isNumArray(geometry.root, 2) || geometry.root[0] !== 320 || geometry.root[1] !== 500)
      fail('geometry.root must be [320, 500]');
    if (
      !isNumArray(geometry.pivot, 2) ||
      geometry.pivot[0] !== N1_PIVOT.x ||
      geometry.pivot[1] !== N1_PIVOT.y
    )
      fail(`geometry.pivot must be [${N1_PIVOT.x}, ${N1_PIVOT.y}]`);
  }

  const actions = isObj(input.actions) ? input.actions : undefined;
  if (!actions) fail('actions missing');
  else {
    const ids = Object.keys(actions);
    for (const id of N1_ACTIONS) if (!(id in actions)) fail(`action ${id} missing`);
    for (const id of ids)
      if (!(N1_ACTIONS as readonly string[]).includes(id)) fail(`unexpected action ${id}`);
    for (const id of N1_ACTIONS) {
      const a = actions[id];
      if (!isObj(a)) continue;
      const p = (msg: string) => fail(`${id}: ${msg}`);
      if (a.facing !== 'left') p('facing must be left (rights are runtime mirrors)');
      if (a.mirror !== true) p('mirror must be true');
      if (a.kind !== 'loop' && a.kind !== 'transition') p('kind must be loop|transition');
      if (typeof a.loop !== 'boolean') p('loop must be boolean');
      if (a.masterFrames !== N1_MASTER_FRAMES) p(`masterFrames must be ${N1_MASTER_FRAMES}`);
      if (
        !isObj(a.review) ||
        !isNum(a.review.frameMs) ||
        !isNum(a.review.durationMs) ||
        !isNum(a.review.fps)
      )
        p('review timing incomplete');
      if (!isNum(a.feetLineY)) p('feetLineY missing');
      const aerial = N1_AERIAL.includes(id);
      if (aerial) {
        if (!isObj(a.rootLift) || !isNumArray(a.rootLift.master, N1_MASTER_FRAMES))
          p(`rootLift.master must have ${N1_MASTER_FRAMES} entries`);
      } else if (a.rootLift !== null) p('rootLift must be null for grounded actions');
      const cycle = isObj(a.cycle) ? a.cycle : null;
      if (a.cycle !== null && a.cycle !== undefined && !cycle) p('cycle must be an object or null');
      if (cycle) {
        if (cycle.kind !== 'loop' && cycle.kind !== 'one-shot')
          p('cycle.kind must be loop|one-shot');
        if ((cycle.kind === 'loop') !== (a.kind === 'loop'))
          p('cycle.kind must match the action kind');
        if (typeof cycle.pipeline !== 'string') p('cycle.pipeline missing');
        if (!isNum(cycle.frames) || cycle.frames < 2) p('cycle.frames invalid');
        if (!isNumArray(cycle.order) || cycle.order.length < 2) p('cycle.order invalid');
        else if (!isNum(cycle.anchor) || cycle.order[0] !== cycle.anchor)
          p('cycle.order must open with the anchor key (the chain contact)');
      }
      const tiers = isObj(a.tiers) ? a.tiers : undefined;
      if (!tiers) p('tiers missing');
      else
        for (const [tier, n] of Object.entries(N1_TIERS) as [N1Tier, number][]) {
          const t = tiers[tier];
          if (!isObj(t)) {
            p(`tier ${tier} missing`);
            continue;
          }
          if (cycle && isNum(cycle.frames)) {
            // a rebuilt action: smooth shows every cycle frame, keys half of them — every other one
            // for a loop, spread so both contacts survive for a one-shot
            const expectedFrames = tier === 'smooth' ? cycle.frames : Math.floor(cycle.frames / 2);
            if (t.frames !== expectedFrames)
              p(
                `tier ${tier} must have ${expectedFrames} frames for a ${cycle.frames}-frame cycle`,
              );
            if (t.masterIndices !== null) p(`tier ${tier} masterIndices must be null for a cycle`);
            if (!isNumArray(t.cycleIndices, expectedFrames) || t.cycleIndices[0] !== 0)
              p(`tier ${tier} cycleIndices must have ${expectedFrames} entries starting at 0`);
            else if (
              cycle.kind === 'one-shot' &&
              t.cycleIndices[expectedFrames - 1] !== cycle.frames - 1
            )
              p(`tier ${tier} cycleIndices must end on the last contact frame ${cycle.frames - 1}`);
          } else {
            if (t.frames !== n) p(`tier ${tier} must have ${n} frames`);
            if (!isNumArray(t.masterIndices, n))
              p(`tier ${tier} masterIndices must have ${n} entries`);
            else {
              const expected = expectedMasterIndices(n);
              if (t.masterIndices.some((v, i) => v !== expected[i]))
                p(`tier ${tier} masterIndices differ from round(i·79/${n - 1})`);
            }
          }
          if (!isNum(t.frameMs) || t.frameMs <= 0) p(`tier ${tier} frameMs invalid`);
          const liftLen = isNum(t.frames) ? t.frames : n;
          if (aerial ? !isNumArray(t.rootLift, liftLen) : t.rootLift !== null)
            p(`tier ${tier} rootLift ${aerial ? `must have ${liftLen} entries` : 'must be null'}`);
        }
      const contacts = isObj(a.contacts) ? a.contacts : undefined;
      if (!contacts) p('contacts missing');
      else
        for (const pos of ['first', 'last'] as const) {
          const c = contacts[pos];
          if (c === null) continue;
          if (
            !isObj(c) ||
            !(N1_ACTIONS as readonly string[]).includes(String(c.action)) ||
            !isNum(c.frame) ||
            typeof c.mirrored !== 'boolean'
          )
            p(`contacts.${pos} malformed`);
        }
    }
  }

  const tiers = isObj(input.tiers) ? input.tiers : undefined;
  if (!tiers) fail('tiers missing');
  else
    for (const label of N1_TIER_LABELS) {
      const t = tiers[label];
      if (!isObj(t)) {
        fail(`tiers.${label} missing`);
        continue;
      }
      const { tier, scale } = tierOfLabel(label);
      if (t.tier !== tier) fail(`tiers.${label}.tier must be ${tier}`);
      if (t.scale !== scale) fail(`tiers.${label}.scale must be ${scale}`);
      if (t.page !== 640 * scale) fail(`tiers.${label}.page must be ${640 * scale}`);
      if (!isNumArray(t.root, 2) || t.root[0] !== 320 * scale || t.root[1] !== 500 * scale)
        fail(`tiers.${label}.root must be [${320 * scale}, ${500 * scale}]`);
      if (typeof t.multiatlas !== 'string' || !t.multiatlas.endsWith('.multiatlas.json'))
        fail(`tiers.${label}.multiatlas must point at a .multiatlas.json`);
      if (!Array.isArray(t.pages) || t.pages.length === 0) fail(`tiers.${label}.pages empty`);
      if (t.framesPerAction !== N1_TIERS[tier])
        fail(`tiers.${label}.framesPerAction must be ${N1_TIERS[tier]}`);
      // Σ per-action frames (cycles declare their own count); only checkable once the actions parsed.
      if (actions && problems.length === 0) {
        const expected = tierFrameCount(input as unknown as N1Manifest, tier);
        if (t.frameCount !== expected)
          fail(`tiers.${label}.frameCount must be ${expected} (the sum over the actions)`);
      }
    }

  if (!isObj(input.chains) || Object.keys(input.chains).length === 0) fail('chains missing');
  if (!isObj(input.hitboxes)) fail('hitboxes missing');

  if (problems.length) throw new N1ManifestError(problems);
  return input as unknown as N1Manifest;
}
